/**
 * Shared editor-page layout constants.
 *
 * The Process editor has three chrome regions that need to agree on a
 * single height value so they stack cleanly:
 *
 *   - {@link ProcessEditorTopBar} (the page's chrome — breadcrumb +
 *     save status + theme toggle + profile)
 *   - the right-hand {@link ProcessSidePanel} dock — anchored to the
 *     viewport with `position: fixed; top: <topbar>` so it starts
 *     flush against the bottom of the topbar
 *   - the left-hand {@link NodeLibraryFlyout} — slides out of the
 *     collapsed rail and similarly docks below the topbar
 *
 * Keeping the value in one place means a topbar-height tweak only
 * needs a single update and prevents a 1px misalignment seam between
 * the dock, the flyout, and the topbar's bottom border.
 *
 * Value derivation: shared `@aibuild/ui` `TopBar` is `min-height: 56px`
 * with vertical padding `12px` (already inside min-height) and a 1px
 * `border-bottom`. In practice the actions row (avatar, icon-buttons)
 * pushes the content height to ~60px; we round to 60px content + 1px
 * border = 61px, which matches the prototype's `.ops-panel { top:
 * 60px }` (their topbar is 60px content + 0px border).
 */
export const EDITOR_TOPBAR_HEIGHT = 61;

/** `md` IconButton box from `@aibuild/ui` — the size every chrome toggle uses. */
const PANEL_TOGGLE_SIZE = 30;

/** Vertical padding above the first control of a panel header or rail. */
export const EDITOR_PANEL_HEADER_PAD = 10;

/**
 * Distance from the top of a chrome region (dock, assistant panel, and their
 * collapsed rails — all pinned to {@link EDITOR_TOPBAR_HEIGHT}) to the centre
 * of its toggle. The dock's collapsed handle is absolutely positioned, so it
 * only lines up with the in-flow header toggles if it reads the same number.
 */
export const EDITOR_PANEL_TOGGLE_CENTER =
  EDITOR_PANEL_HEADER_PAD + PANEL_TOGGLE_SIZE / 2;
